"use client"

import type React from "react"
import { useState } from "react"
import {
  FiSearch,
  FiFilter,
  FiX,
  FiRefreshCw,
  FiEdit
} from "react-icons/fi"
import { LuEye } from "react-icons/lu"
import { Link } from "react-router-dom"
import { Pagination } from "../../../utils/pagination.utils"
import { ExcelDownload } from "../../../utils/excel.utils"
import { useAuth } from "../../../provider/auth.provider"
import { formatDate } from "../../activities/edit"
import axiosInstance from "../../../libs/axios"

import { FaClock, FaCheckCircle, FaTimesCircle, FaUser, FaSearch, FaFileExcel, FaExclamationCircle, FaMapMarkerAlt, FaCalendarAlt, FaSort, FaSortUp, FaSortDown } from "react-icons/fa"
import { SelectWithSearch2, type SelectOption } from "../../../utils/select-with-search"
import { useDebouncedCallback } from "../../../hooks/useDebounce"

/**
 * Query params a dashboard tile (or a shared link) may pre-apply to this list.
 * `status` and `search` seed the header controls; the rest seed the filter panel.
 */
export const URL_FILTER_KEYS = [
  "status",
  "search",
  "project_id",
  "activity_id",
  "engineer_id",
  "trip_start_date",
  "trip_end_date",
];

interface CabHiring {
  id: number
  engineer_id: number
  association_type: string
  trip_purpose: string
  trip_start_date: string
  trip_end_date?: string | null
  vehicle_number?: string | null
  driver_name?: string | null
  driver_number?: string | null
  cab_start_reading: number
  cab_start_number_selfie: string
  cab_start_model_selfie: string
  cab_end_reading?: number | null
  cab_end_number_selfie?: string | null
  cab_end_model_selfie?: string | null
  cab_start_latitude?: string | null
  cab_start_longitude?: string | null
  start_latitude?: string | null
  start_longitude?: string | null
  cab_end_latitude?: string | null
  cab_end_longitude?: string | null
  end_latitude?: string | null
  end_longitude?: string | null
  running_kms?: number | null
  toll_charges?: number | null
  other_charges?: number | null
  hiring_total_amount?: number | null
  hiring_per_km_amount?: number | null
  hiring_remarks?: string | null
  approved_amount?: number | null
  bill_photo?: string | null
  toll_photo?: string | null
  payble_entity?: string | null
  payble_entity_name?: string | null
  status: string
  approved_by?: number | null
  approved_at?: string | null
  rejection_reason?: string | null
  rejected_at?: string | null
  rejected_by?: number | null
  created_at: string
  updated_at: string
  project_site_id?: number | null
  engineer: {
    employee_code: string
    id: number
    first_name: string
    last_name: string
    user_projects?: Array<{
      id: number
      project_id: number
      project?: { id: number; project_name: string; project_short_name: string }
    }>
  }
  rejector?: {
    first_name?: string
    last_name?: string
  } | null
  approver?: {
    id: number
    first_name: string
    last_name: string
    association_type: string
  } | null
  cab_visit: Array<{
    id: number
    project_site_id?: number | null
    cab_hiring_id: number
    visit_number: number
    visit_selfie_with_cab: string
    visit_client_proof: string
    created_at: string
    updated_at: string
    project_site?: {
      id: number
      site_id: string
      site_name: string
    } | null
  }>
}

interface PaginationType {
  total_count: number
  page: number
  limit: number
  total_pages: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

export interface CabHiringFilters {
  project_id?: string
  project_name?: string
  trip_start_date?: string
  trip_end_date?: string
  engineer_id?: string
  engineer_name?: string
  activity_id?: string
  activity_name?: string
}

/** Filter keys that are sent to the API; the *_name keys only label the selected option. */
const CAB_HIRING_FILTER_PARAM_KEYS = [
  "project_id",
  "trip_start_date",
  "trip_end_date",
  "engineer_id",
  "activity_id",
] as const

/** Strip label-only keys and empty values so only real filters reach the API. */
export const toCabHiringQueryParams = (filters: CabHiringFilters = {}): Record<string, string> => {
  const params: Record<string, string> = {}
  CAB_HIRING_FILTER_PARAM_KEYS.forEach((key) => {
    const value = filters[key]
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params[key] = String(value).trim()
    }
  })
  return params
}

const countActiveFilters = (filters: CabHiringFilters = {}) =>
  Object.keys(toCabHiringQueryParams(filters)).length

// Module-level so SelectWithSearch2 sees stable references (its fetcher
// depends on extraParams / mapOption identity).
const ASSIGNED_PROJECTS_PARAMS = { assigned_only: true }
const mapProjectOption = (project: any): SelectOption => ({
  id: project.id,
  value: project.id,
  label: project.project_short_name
    ? `${project.project_name} (${project.project_short_name})`
    : project.project_name || `Project #${project.id}`,
})
const mapEngineerOption = (user: any): SelectOption => {
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || `ID ${user.id}`
  return {
    id: user.id,
    value: user.id,
    label: user.employee_code ? `${name} (${user.employee_code})` : name,
  }
}
const mapActivityOption = (activity: any): SelectOption => ({
  id: activity.id,
  value: activity.id,
  label: activity.activity_name || `Activity #${activity.id}`,
})

const selectedOption = (id?: string, label?: string, fallback = "ID"): SelectOption | null =>
  id ? { id: Number(id), value: Number(id), label: label || `${fallback} #${id}` } : null

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: CabHiringFilters
  onFiltersChange: (filters: CabHiringFilters) => void
  onApply: () => void
  onReset: () => void
}

const inputClass =
  "w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"

const CabHiringFilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset
}) => {
  const { user } = useAuth()
  // Super admin (role level 1) may filter by any project; everyone else only
  // by the projects they are assigned to.
  const isSuperAdmin = user?.role?.level === 1

  if (!isOpen) return null

  const update = (patch: Partial<CabHiringFilters>) => {
    const next: CabHiringFilters = { ...filters, ...patch }
    ;(Object.keys(next) as (keyof CabHiringFilters)[]).forEach((key) => {
      if (next[key] === undefined || next[key] === "") delete next[key]
    })
    onFiltersChange(next)
  }

  const dateRangeInvalid =
    !!filters.trip_start_date && !!filters.trip_end_date && filters.trip_end_date < filters.trip_start_date

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content — single view, no tabs */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
              <SelectWithSearch2
                placeholder="Search project"
                value={selectedOption(filters.project_id, filters.project_name, "Project")}
                onChange={(option) =>
                  update({
                    project_id: option ? String(option.value) : undefined,
                    project_name: option ? option.label : undefined,
                  })
                }
                apiEndpoint="/projects/list"
                dataKey="projects"
                labelKey="project_name"
                minSearchLength={0}
                extraParams={isSuperAdmin ? undefined : ASSIGNED_PROJECTS_PARAMS}
                mapOption={mapProjectOption}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
              <SelectWithSearch2
                placeholder="Search activity"
                value={selectedOption(filters.activity_id, filters.activity_name, "Activity")}
                onChange={(option) =>
                  update({
                    activity_id: option ? String(option.value) : undefined,
                    activity_name: option ? option.label : undefined,
                  })
                }
                apiEndpoint="/claims/cab-hiring/filter-options/activities"
                dataKey="activities"
                labelKey="activity_name"
                minSearchLength={0}
                mapOption={mapActivityOption}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Trip Start Date</label>
              <input
                type="date"
                className={inputClass}
                value={filters.trip_start_date || ""}
                max={filters.trip_end_date || undefined}
                onChange={(e) => update({ trip_start_date: e.target.value || undefined })}
              />
              <p className="text-xs text-gray-500 mt-1">Trips started on or after this date</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Trip End Date</label>
              <input
                type="date"
                className={inputClass}
                value={filters.trip_end_date || ""}
                min={filters.trip_start_date || undefined}
                onChange={(e) => update({ trip_end_date: e.target.value || undefined })}
              />
              <p className="text-xs text-gray-500 mt-1">Trips ended on or before this date</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Engineer Name</label>
              <SelectWithSearch2
                placeholder="Search engineer by name or code"
                value={selectedOption(filters.engineer_id, filters.engineer_name, "Engineer")}
                onChange={(option) =>
                  update({
                    engineer_id: option ? String(option.value) : undefined,
                    engineer_name: option ? option.label : undefined,
                  })
                }
                apiEndpoint="/users/list"
                minSearchLength={0}
                mapOption={mapEngineerOption}
                className="w-full"
              />
            </div>
          </div>

          {dateRangeInvalid && (
            <p className="text-sm text-red-600 mt-4">Trip End Date must be on or after Trip Start Date.</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={dateRangeInvalid}
              onClick={() => {
                onApply()
                onClose()
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CabHiringListProps {
  trips: CabHiring[]
  pagination: PaginationType
  onSearch: (search: string) => void
  onFilterChange: (filter: string) => void
  statusFilter: string
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAdvancedFilter?: (filters: CabHiringFilters) => void
  loading?: boolean
  sortConfig?: {
    field: string
    direction: "ASC" | "DESC"
  }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  appliedFilters?: CabHiringFilters
  searchValue?: string
}

export const CabHiringList: React.FC<CabHiringListProps> = ({
  trips,
  pagination,
  onSearch,
  onFilterChange,
  statusFilter,
  onPageChange,
  onLimitChange,
  onAdvancedFilter,
  loading = false,
  sortConfig,
  onSort,
  appliedFilters,
  searchValue = "",
}) => {
  const [searchTerm, setSearchTerm] = useState(searchValue)
  const [showExcelModal, setShowExcelModal] = useState(false)
  const { hasPermission } = useAuth()
  // Draft filters edited inside the panel; appliedFilters (from the container) is what the list shows.
  const [filters, setFilters] = useState<CabHiringFilters>(appliedFilters ?? {})
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])
  const [expandedProjectRows, setExpandedProjectRows] = useState<Set<number>>(new Set())
  const [expandedUserProjectRows, setExpandedUserProjectRows] = useState<Set<number>>(new Set())
  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange(e.target.value)
  }

  const openFilterModal = () => {
    // Start from what is applied, so Cancel discards unsaved edits.
    setFilters(appliedFilters ?? {})
    setIsFilterModalOpen(true)
  }

  const handleResetFilters = () => {
    setFilters({})
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
  }

  const getActiveFiltersCount = () => countActiveFilters(appliedFilters)

  const formatCurrency = (amount?: number | null) => {
    if (!amount) return "₹0"
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      in_progress: { icon: FaClock, bgColor: "bg-blue-100", textColor: "text-blue-800", label: "In Progress" },
      completed: { icon: FaCheckCircle, bgColor: "bg-gray-100", textColor: "text-gray-800", label: "Completed" },
      claim_submitted: { icon: FaClock, bgColor: "bg-yellow-100", textColor: "text-yellow-800", label: "Submitted" },
      approved: { icon: FaCheckCircle, bgColor: "bg-green-100", textColor: "text-green-800", label: "Approved" },
      rejected: { icon: FaTimesCircle, bgColor: "bg-red-100", textColor: "text-red-800", label: "Rejected" },
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.in_progress
    const IconComponent = config.icon

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      >
        <IconComponent className="w-2 h-2" />
        {config.label}
      </span>
    )
  }

  const getAssociationBadge = (associationType: string) => {
    const badgeConfig = {
      inhouse: { bgColor: "bg-purple-100", textColor: "text-purple-800", label: "In-house" },
      contractor: { bgColor: "bg-orange-100", textColor: "text-orange-800", label: "Contractor" },
      vendor: { bgColor: "bg-indigo-100", textColor: "text-indigo-800", label: "Vendor" },
    }

    const config = badgeConfig[associationType as keyof typeof badgeConfig] || {
      bgColor: "bg-gray-100",
      textColor: "text-gray-800",
      label: associationType,
    }

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      >
        <FaUser className="w-2 h-2" />
        {config.label}
      </span>
    )
  }

  const getAppliedFilters = () => appliedFilters ?? {}

  const getSearchParam = () => (searchValue ?? "").trim() || searchTerm.trim()

  const getProjectSiteInfo = (trip: CabHiring) => {
    return trip.cab_visit?.find((visit) => visit.project_site)?.project_site || null
  }

  const getAllProjectSites = (trip: CabHiring) => {
    const seen = new Set<number>()
    return (trip.cab_visit ?? [])
      .filter(v => v.project_site)
      .map(v => v.project_site!)
      .filter(ps => {
        if (seen.has(ps.id)) return false
        seen.add(ps.id)
        return true
      })
  }

  // Mirrors the server: hiring_total_amount is stored as km x rate + toll + other charges,
  // so toll / other charges must not be added on top of it again.
  const getCabHiringTotalAmount = (trip: CabHiring) => {
    const runningKms = Number(trip.running_kms ?? 0)
    const perKm = Number(trip.hiring_per_km_amount ?? 0)
    if (runningKms > 0 || perKm > 0) {
      return Math.round(runningKms * perKm + Number(trip.toll_charges ?? 0) + Number(trip.other_charges ?? 0))
    }
    return Number(trip.hiring_total_amount ?? 0)
  }

  const mapTripForExport = (trip: CabHiring) => {
    const engineerName = `${trip.engineer?.first_name || ""} ${trip.engineer?.last_name || ""}`.trim() || "Unknown"
    const projectSite = getProjectSiteInfo(trip)
    const rejectorName = trip.rejector
      ? `${trip.rejector.first_name || ""} ${trip.rejector.last_name || ""}`.trim()
      : ""

    const formatValue = (value?: string | null) => (value ? formatDate(value) : "")

    return {
      "Engineer Name": engineerName,
      "Trip Purpose": trip.trip_purpose,
      "Trip Start": formatDate(trip.trip_start_date),
      "Trip End": formatValue(trip.trip_end_date),
      "Vehicle Number": trip.vehicle_number || "",
      "Driver Name": trip.driver_name || "",
      "Driver Number": trip.driver_number || "",
      "Start Odometer": trip.cab_start_reading ?? "",
      "End Odometer": trip.cab_end_reading ?? "",
      "Running KMs": trip.running_kms ?? 0,
      "Toll Charges": trip.toll_charges ?? 0,
      "Other Charges": trip.other_charges ?? 0,
      "Per KM Amount": trip.hiring_per_km_amount ?? 0,
      "Submitted Total Amount": getCabHiringTotalAmount(trip),
      "Approved Amount": trip.approved_amount ?? "",
      "Final Amount": trip.approved_amount ?? getCabHiringTotalAmount(trip),
      "Visits Count": trip.cab_visit?.length || 0,
      "Project Site": projectSite?.site_name || "",
      Status: trip.status,
      "Approval Date": formatValue(trip.approved_at),
      "Rejection Reason": trip.rejection_reason || "",
      "Rejected By": rejectorName,
      "Remarks": trip.hiring_remarks || "",
      "Bill Photo": trip.bill_photo || "",
      "Toll Photo": trip.toll_photo || "",
      "Created At": formatDate(trip.created_at),
      "Updated At": formatDate(trip.updated_at),
    }
  }

  const fetchExportData = async () => {
    try {
      setExporting(true)
      const baseParams: Record<string, any> = toCabHiringQueryParams(getAppliedFilters())

      const searchParam = getSearchParam()
      if (searchParam) baseParams.search = searchParam
      if (statusFilter) baseParams.status = statusFilter

      if (sortConfig?.field) {
        baseParams.sort_by = sortConfig.field
        baseParams.sort_order = sortConfig.direction
      }

      Object.keys(baseParams).forEach((key) => {
        if (baseParams[key] === undefined || baseParams[key] === null || baseParams[key] === "") {
          delete baseParams[key]
        }
      })

      const allTrips: CabHiring[] = []
      let currentPage = 1
      let totalPages = 1
      const pageSize = Number(pagination?.limit) || 10

      do {
        const params = {
          ...baseParams,
          page: currentPage,
          limit: pageSize,
        }

        const response = await axiosInstance.get("/claims/cab-hiring/list", { params })
        if (!response.data.success) {
          break
        }

        const responseTrips: CabHiring[] = response.data.data.cabHirings || []
        allTrips.push(...responseTrips)

        const paginationInfo = response.data.data.pagination || {}
        totalPages =
          paginationInfo.total_pages ??
          paginationInfo.totalPages ??
          paginationInfo.total_pages ??
          paginationInfo.total_pages ??
          currentPage

        currentPage += 1
      } while (currentPage <= totalPages)

      if (allTrips.length === 0) {
        setExportData([])
      } else {
        setExportData(allTrips.map(mapTripForExport))
      }
      setShowExcelModal(true)
    } catch (error) {
      console.error("Error exporting cab hiring data:", error)
    } finally {
      setExporting(false)
    }
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction =
      sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.field !== field) {
      return <FaSort className="w-3 h-3 text-gray-400" />
    }
    return sortConfig.direction === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-600" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-600" />
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Cab Hiring</h2>
            <p className="text-sm text-gray-600 mt-1">Manage cab hiring trips and visits</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by engineer name or status..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full sm:w-72"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
            </div>

            <button
              onClick={openFilterModal}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded transition-colors text-sm font-medium ${getActiveFiltersCount() > 0
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>

            <select
              value={statusFilter}
              onChange={handleFilterChange}
              className="px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
            >
              <option value="">All Status</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="claim_submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? "Preparing..." : "Export"}
            </button>
          </div>
        </div>
      </div>

      <CabHiringFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* Stats */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Trips</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{pagination.total_count}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
              <FaCar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {trips?.filter(t => t.status === 'in_progress')?.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
              <FaClock className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Submitted</p>
              <p className="text-2xl font-bold text-yellow-900 mt-1">
                {trips?.filter(t => t.status === 'claim_submitted')?.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded flex items-center justify-center">
              <FaClock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {trips?.filter(t => t.status === 'approved')?.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
              <FaCheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div> */}

      {/* Table */}
      <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Engineer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Eng. Projects
                </th>
                {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th> */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("trip_purpose")}
                  >
                    <span>Trip Purpose</span>
                    {getSortIcon("trip_purpose")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project Site
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Site Id
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("running_kms")}
                  >
                    <span>Distance</span>
                    {getSortIcon("running_kms")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visits
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("hiring_total_amount")}
                  >
                    <span>Submitted Amount</span>
                    {getSortIcon("hiring_total_amount")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Approved Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Approver Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("trip_start_date")}
                  >
                    <span>Trip Start Date</span>
                    {getSortIcon("trip_start_date")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("trip_end_date")}
                  >
                    <span>Trip End Date</span>
                    {getSortIcon("trip_end_date")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Approved Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("status")}
                  >
                    <span>Status</span>
                    {getSortIcon("status")}
                  </button>
                </th>
                
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={16} className="px-4 py-12">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-600 text-sm">Loading trips...</p>
                    </div>
                  </td>
                </tr>
              ) : !Array.isArray(trips) || trips.length === 0 ? (
                <tr>
                  <td colSpan={16} className="px-4 py-12">
                    <div className="flex flex-col items-center justify-center">
                      <FaExclamationCircle className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 text-sm">No trips found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                (Array.isArray(trips) ? trips : []).map((trip) => {
                  const projectSite = getProjectSiteInfo(trip)
                  return (
                    <tr key={trip.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {hasPermission('cab_claim', 'read') && (
                            <Link
                              to={`/claims/cab-hiring/${trip.id}`}
                              className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors inline-block"
                            >
                              <LuEye className="w-4 h-4" />
                            </Link>
                          )}
                          {hasPermission('cab_claim', 'submit') && trip.status === 'claim_submitted' && (
                            <Link
                              to={`/claims/cab-hiring/${trip.id}?edit=true`}
                              className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded transition-colors inline-block"
                              title="Edit submitted claim"
                            >
                              <FiEdit className="w-4 h-4" />
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-900">
                          {trip.engineer.first_name} {trip.engineer.last_name}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-900">
                          {trip.engineer?.employee_code}
                        </span>
                      </td>
                      <td className="px-4 py-4 max-w-[200px]">
                        {(() => {
                          const userProjects = trip.engineer?.user_projects ?? []
                          const isExpanded = expandedUserProjectRows.has(trip.id)
                          const visible = isExpanded ? userProjects : userProjects.slice(0, 2)
                          if (userProjects.length === 0) return <span className="text-sm text-gray-400 italic">—</span>
                          return (
                            <div className="flex flex-col gap-0.5">
                              {visible.map(up => (
                                <span key={up.id} className="text-xs font-medium text-gray-900 leading-snug">
                                  {up.project?.project_short_name ?? up.project?.project_name ?? `Project #${up.project_id}`}
                                </span>
                              ))}
                              {userProjects.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedUserProjectRows(prev => {
                                    const next = new Set(prev)
                                    isExpanded ? next.delete(trip.id) : next.add(trip.id)
                                    return next
                                  })}
                                  className="text-xs text-blue-600 hover:underline text-left mt-0.5"
                                >
                                  {isExpanded ? 'View Less' : `+${userProjects.length - 2} more`}
                                </button>
                              )}
                            </div>
                          )
                        })()}
                      </td>
                      {/* <td className="px-4 py-4 whitespace-nowrap">
                        {getAssociationBadge(trip.association_type)}
                      </td> */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-900 max-w-[200px] truncate block" title={trip.trip_purpose}>
                          {trip.trip_purpose}
                        </span>
                      </td>
                      <td className="px-4 py-4 max-w-[200px]">
                        {(() => {
                          const allSites = getAllProjectSites(trip)
                          const isExpanded = expandedProjectRows.has(trip.id)
                          const visible = isExpanded ? allSites : allSites.slice(0, 2)
                          if (allSites.length === 0) return <span className="text-sm text-gray-400 italic">No site</span>
                          return (
                            <div className="flex flex-col gap-0.5">
                              {visible.map(ps => (
                                <span key={ps.id} className="text-xs font-medium text-gray-900 leading-snug">{ps.site_name}</span>
                              ))}
                              {allSites.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedProjectRows(prev => {
                                    const next = new Set(prev)
                                    isExpanded ? next.delete(trip.id) : next.add(trip.id)
                                    return next
                                  })}
                                  className="text-xs text-blue-600 hover:underline text-left mt-0.5"
                                >
                                  {isExpanded ? 'View Less' : `+${allSites.length - 2} more`}
                                </button>
                              )}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{projectSite?.site_id || '—'}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{trip.running_kms ? `${trip.running_kms} km` : "-"}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FaMapMarkerAlt className="w-3 h-3 text-gray-400" />
                          <span className="text-sm text-gray-900">{trip.cab_visit?.length || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-gray-900">
                            {getCabHiringTotalAmount(trip) ? formatCurrency(getCabHiringTotalAmount(trip)) : 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {trip.approved_amount != null ? (
                          <div className="text-sm font-semibold text-gray-600">
                            {formatCurrency(trip.approved_amount)}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {trip.approver ? (
                          <span className="text-sm font-medium text-gray-900">
                            {trip.approver.first_name} {trip.approver.last_name}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {trip.start_latitude && trip.start_longitude ? (
                          <span className="text-sm text-gray-600 cursor-pointer hover:text-blue-600" title={`${trip.start_latitude}, ${trip.start_longitude}`}>
                             {parseFloat(trip.start_latitude).toFixed(2)}, {parseFloat(trip.start_longitude).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {trip.end_latitude && trip.end_longitude ? (
                          <span className="text-sm text-gray-600 cursor-pointer hover:text-blue-600" title={`${trip.end_latitude}, ${trip.end_longitude}`}>
                             {parseFloat(trip.end_latitude).toFixed(2)}, {parseFloat(trip.end_longitude).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                          <span className="text-sm text-gray-600">{formatDate(trip.trip_start_date)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {trip.trip_end_date ? (
                          <div className="flex items-center gap-2">
                            <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-600">{formatDate(trip.trip_end_date)}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {trip.approved_at ? (
                          <div className="flex items-center gap-2">
                            <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-600">{formatDate(trip.approved_at)}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(trip.status)}</td>

                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination pagination={pagination as any} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="cab_hiring"
        title="Export Cab Hiring Data"
        description="Download complete cab hiring details"
      />
    </div>
  )
}
